import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Users,
  Loader2,
  AlertCircle,
  Copy,
  Monitor,
  MonitorOff,
  Clock,
  RefreshCw,
  WifiOff,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Maximum reconnection attempts
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;

const MeetingRoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [step, setStep] = useState('loading'); // loading, notStarted, join, waiting, meeting, ended, connectionFailed
  const [name, setName] = useState('');
  const [role, setRole] = useState('guest');
  const [userId, setUserId] = useState('');
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [remoteParticipant, setRemoteParticipant] = useState(null);
  const [timeUntilStart, setTimeUntilStart] = useState(null);
  const [iceServers, setIceServers] = useState([]);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [knockRequests, setKnockRequests] = useState([]); // pending guest knocks
  const knockPollingRef = useRef(null);
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);
  const isJoiningRef = useRef(false);
  const hasJoinedRef = useRef(false);

  // Poll for guest knock requests (host only)
  useEffect(() => {
    if (role !== 'host' || !meetingInfo?.id) return;
    const token = localStorage.getItem('crm_token');
    if (!token) return;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/guest/meeting/${meetingInfo.id}/pending-knocks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.knocks && data.knocks.length > 0) {
            setKnockRequests(data.knocks);
          }
        }
      } catch (e) {
        console.warn('Knock poll error:', e);
      }
    };

    poll();
    knockPollingRef.current = setInterval(poll, 4000);
    return () => clearInterval(knockPollingRef.current);
  }, [role, meetingInfo]);

  const handleAdmit = async (knock, approved) => {
    const token = localStorage.getItem('crm_token');
    try {
      await fetch(`${API_URL}/guest/meeting/${meetingInfo.id}/knock-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestIdentity: knock.guestIdentity, approved })
      });
    } catch (e) {
      console.warn('Admit error:', e);
    }
    setKnockRequests(prev => prev.filter(k => k.guestIdentity !== knock.guestIdentity));
  };

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('crm_token');
      
      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setName(userData.name || 'Host');
            setRole('host');
            setUserId(userData.id || `host_${Date.now()}`);
            return;
          }
        } catch (e) {
          console.warn('Auth check failed:', e);
        }
      }
      
      setRole('guest');
      setUserId(`guest_${Date.now()}`);
    };
    
    checkAuth();
  }, []);
  
  // Fetch meeting info and ICE servers
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch meeting info
        const meetingRes = await fetch(`${API_URL}/api/meetings/room/${roomId}`);
        if (!meetingRes.ok) {
          const data = await meetingRes.json();
          setError(data.detail || 'Meeting not found');
          setStep('error');
          return;
        }
        const meetingData = await meetingRes.json();
        setMeetingInfo(meetingData);
        
        // Check if meeting can be joined
        if (!meetingData.canJoin) {
          setTimeUntilStart(meetingData.timeUntilStart);
          setStep('notStarted');
          return;
        }
        
        // Fetch ICE servers
        const iceRes = await fetch(`${API_URL}/api/meetings/ice-servers`);
        if (iceRes.ok) {
          const iceData = await iceRes.json();
          console.log('ICE servers loaded:', iceData.iceServers?.length || 0);
          setIceServers(iceData.iceServers || []);
        }
        
        setStep('join');
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Failed to load meeting information. Please refresh the page.');
        setStep('error');
      }
    };
    
    fetchData();
  }, [roomId]);

  // Countdown timer for scheduled meetings
  useEffect(() => {
    if (step !== 'notStarted' || !timeUntilStart) return;
    
    const interval = setInterval(() => {
      setTimeUntilStart(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setStep('join');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [step, timeUntilStart]);

  // Format countdown time
  const formatCountdown = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Create WebRTC peer connection
  const createPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    const config = {
      iceServers: iceServers.length > 0 ? iceServers : [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
      iceCandidatePoolSize: 10,
      iceTransportPolicy: 'all',
    };

    console.log('Creating peer connection with', config.iceServers.length, 'ICE servers');
    const pc = new RTCPeerConnection(config);

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        console.log('Sending ICE candidate');
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          candidate: event.candidate,
        }));
      }
    };

    pc.ontrack = (event) => {
      console.log('Received remote track:', event.track.kind);
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      setConnectionStatus(pc.iceConnectionState);
      
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        toast.success('Connected!');
        setReconnectAttempts(0); // Reset on successful connection
      } else if (pc.iceConnectionState === 'disconnected') {
        toast.warning('Connection interrupted...');
      } else if (pc.iceConnectionState === 'failed') {
        console.error('ICE connection failed');
        // Try ICE restart instead of full reconnect
        if (pc.restartIce) {
          console.log('Attempting ICE restart');
          pc.restartIce();
        }
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
    };

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Adding local track:', track.kind);
        pc.addTrack(track, localStreamRef.current);
      });
    }

    peerConnectionRef.current = pc;
    return pc;
  }, [iceServers]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback(async (event) => {
    const data = JSON.parse(event.data);
    console.log('WS message:', data.type);

    switch (data.type) {
      case 'room_state':
        setParticipants(data.participants || []);
        if (data.has_host || role === 'host') {
          setStep('meeting');
        } else {
          setStep('waiting');
        }
        break;

      case 'user_joined':
        setParticipants(prev => {
          if (prev.find(p => p.user_id === data.user_id)) return prev;
          return [...prev, { user_id: data.user_id, role: data.role, name: data.name }];
        });
        setRemoteParticipant({ user_id: data.user_id, name: data.name, role: data.role });
        
        if (data.role === 'host') {
          setStep('meeting');
        }
        
        toast.success(`${data.name} joined`);
        
        // If we're the initiator (host or first joiner), create offer
        if (role === 'host' || participants.length === 0) {
          setTimeout(async () => {
            try {
              const pc = peerConnectionRef.current || createPeerConnection();
              const offer = await pc.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: true
              });
              await pc.setLocalDescription(offer);
              wsRef.current?.send(JSON.stringify({
                type: 'offer',
                offer: pc.localDescription,
                target_id: data.user_id
              }));
            } catch (e) {
              console.error('Error creating offer:', e);
            }
          }, 1000);
        }
        break;

      case 'user_left':
        setParticipants(prev => prev.filter(p => p.user_id !== data.user_id));
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = null;
        }
        setRemoteParticipant(null);
        toast.info(`${data.name || 'Participant'} left`);
        break;

      case 'offer':
        try {
          let pc = peerConnectionRef.current;
          if (!pc || pc.signalingState === 'closed') {
            pc = createPeerConnection();
          }
          
          await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
          
          // Process pending candidates
          for (const candidate of pendingCandidatesRef.current) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          }
          pendingCandidatesRef.current = [];
          
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          
          wsRef.current?.send(JSON.stringify({
            type: 'answer',
            answer: pc.localDescription,
            target_id: data.from_id
          }));
        } catch (e) {
          console.error('Error handling offer:', e);
        }
        break;

      case 'answer':
        try {
          const pc = peerConnectionRef.current;
          if (pc && pc.signalingState !== 'stable') {
            await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
            
            // Process pending candidates
            for (const candidate of pendingCandidatesRef.current) {
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
            }
            pendingCandidatesRef.current = [];
          }
        } catch (e) {
          console.error('Error handling answer:', e);
        }
        break;

      case 'ice_candidate':
        if (data.candidate) {
          try {
            const pc = peerConnectionRef.current;
            if (pc && pc.remoteDescription) {
              await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
            } else {
              pendingCandidatesRef.current.push(data.candidate);
            }
          } catch (e) {
            console.warn('Error adding ICE candidate:', e);
          }
        }
        break;

      case 'meeting_ended':
        setStep('ended');
        toast.info('Meeting has ended');
        cleanup();
        break;

      case 'pong':
        // Heartbeat response received
        break;

      case 'error':
        console.error('Server error:', data.message);
        setError(data.message);
        toast.error(data.message);
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  }, [role, participants, createPeerConnection]);

  // Start media
  const startMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      console.error('Media error:', err);
      
      // Try video only
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        toast.warning('Microphone unavailable');
        return stream;
      } catch (e) {
        // Try audio only
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          localStreamRef.current = stream;
          setIsVideoOff(true);
          toast.warning('Camera unavailable');
          return stream;
        } catch (e2) {
          toast.warning('No camera or mic - joining in listen-only mode');
          return null;
        }
      }
    }
  };

  // Join meeting - with proper reconnection handling
  const joinMeeting = async () => {
    // Prevent multiple simultaneous join attempts
    if (isJoiningRef.current) {
      console.log('Join already in progress, skipping');
      return;
    }
    
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    isJoiningRef.current = true;
    setIsConnecting(true);
    setError(null);
    
    try {
      // Start media
      await startMedia();
      
      // Create peer connection
      createPeerConnection();
      
      // Connect WebSocket
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      console.log('Connecting to:', `${wsUrl}/ws/meeting/${roomId}`);
      
      const ws = new WebSocket(`${wsUrl}/ws/meeting/${roomId}`);
      
      // Connection timeout
      const timeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('Connection timeout');
          ws.close();
          isJoiningRef.current = false;
          setIsConnecting(false);
          handleConnectionFailure('Connection timeout. Please check your internet connection.');
        }
      }, 15000);
      
      ws.onopen = () => {
        console.log('WebSocket connected successfully');
        clearTimeout(timeout);
        
        ws.send(JSON.stringify({
          user_id: userId,
          role: role,
          name: name
        }));
        
        hasJoinedRef.current = true;
        setIsConnecting(false);
        setReconnectAttempts(0); // Reset reconnect counter on successful connection
        
        // Heartbeat to keep connection alive
        const heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(heartbeat);
          }
        }, 25000);
        
        ws.heartbeatInterval = heartbeat;
        isJoiningRef.current = false;
      };
      
      ws.onmessage = handleWebSocketMessage;
      
      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
        clearTimeout(timeout);
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        clearTimeout(timeout);
        if (ws.heartbeatInterval) clearInterval(ws.heartbeatInterval);
        isJoiningRef.current = false;
        
        // Don't reconnect if meeting ended or user left intentionally
        if (step === 'ended' || step === 'join') {
          return;
        }
        
        setConnectionStatus('disconnected');
        
        // Only attempt reconnect if we had successfully joined before
        if (hasJoinedRef.current && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          const nextAttempt = reconnectAttempts + 1;
          setReconnectAttempts(nextAttempt);
          toast.info(`Reconnecting... (${nextAttempt}/${MAX_RECONNECT_ATTEMPTS})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            joinMeeting();
          }, RECONNECT_DELAY);
        } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          // Max reconnects reached
          handleConnectionFailure('Connection lost. Maximum reconnection attempts reached.');
        }
      };
      
      wsRef.current = ws;
      
    } catch (err) {
      console.error('Join error:', err);
      isJoiningRef.current = false;
      setIsConnecting(false);
      handleConnectionFailure(err.message || 'Failed to join meeting');
    }
  };

  // Handle connection failure
  const handleConnectionFailure = (message) => {
    setError(message);
    setStep('connectionFailed');
    cleanup();
  };

  // Manual retry
  const retryConnection = () => {
    setReconnectAttempts(0);
    hasJoinedRef.current = false;
    setError(null);
    setStep('join');
  };

  // Controls
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = isMuted;
        setIsMuted(!isMuted);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = isVideoOff;
        setIsVideoOff(!isVideoOff);
      }
    }
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
      
      if (peerConnectionRef.current && localStreamRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender && videoTrack) {
          await sender.replaceTrack(videoTrack);
        }
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }
      }
      
      setIsScreenSharing(false);
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always' }
        });
        
        screenStreamRef.current = screenStream;
        const screenTrack = screenStream.getVideoTracks()[0];
        
        if (peerConnectionRef.current) {
          const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
          if (sender) await sender.replaceTrack(screenTrack);
        }
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }
        
        screenTrack.onended = () => toggleScreenShare();
        setIsScreenSharing(true);
      } catch (e) {
        console.error('Screen share error:', e);
        toast.error('Failed to share screen');
      }
    }
  };

  const endMeeting = async () => {
    if (role === 'host') {
      wsRef.current?.send(JSON.stringify({ type: 'end_meeting' }));
      
      if (meetingInfo?.id) {
        try {
          toast.info('Generating summary...');
          const token = localStorage.getItem('crm_token');
          await fetch(`${API_URL}/api/meetings/${meetingInfo.id}/generate-summary`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('Summary generated!');
        } catch (e) {
          console.error('Summary error:', e);
        }
      }
    }
    cleanup();
    setStep('ended');
  };

  const leaveMeeting = () => {
    cleanup();
    navigate('/leads-board');
  };

  const cleanup = () => {
    console.log('Cleaning up meeting resources');
    hasJoinedRef.current = false;
    isJoiningRef.current = false;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(t => t.stop());
      screenStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanup();
  }, []);

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Toaster position="top-center" />
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-red-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading meeting...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2 text-white">Unable to Join</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connection failed state (after max retries)
  if (step === 'connectionFailed') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <WifiOff className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2 text-white">Connection Failed</h2>
            <p className="text-gray-400 mb-4">{error || 'Unable to establish connection to the meeting.'}</p>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Troubleshooting tips:</p>
              <ul className="text-xs text-gray-500 text-left list-disc pl-5 space-y-1">
                <li>Check your internet connection</li>
                <li>Try disabling VPN if enabled</li>
                <li>Clear browser cache and refresh</li>
                <li>Try a different browser (Chrome recommended)</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-center mt-6">
              <Button variant="outline" onClick={() => navigate('/')} className="border-gray-600 text-gray-300">
                Go Home
              </Button>
              <Button onClick={retryConnection} className="bg-red-600 hover:bg-red-700">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Meeting not started yet
  if (step === 'notStarted') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2 text-white">Meeting Not Started Yet</h2>
            <p className="text-gray-400 mb-4">
              This meeting is scheduled to start in:
            </p>
            <div className="text-4xl font-bold text-red-500 mb-4">
              {formatCountdown(timeUntilStart)}
            </div>
            {meetingInfo && (
              <div className="bg-gray-700/50 p-4 rounded-lg text-left mb-4">
                <p className="text-sm text-gray-400">Meeting: <span className="text-white">{meetingInfo.leadName}</span></p>
                <p className="text-sm text-gray-400">Host: <span className="text-white">{meetingInfo.hostName}</span></p>
                <p className="text-sm text-gray-400">
                  Scheduled: <span className="text-white">
                    {new Date(meetingInfo.scheduledDateTime).toLocaleString()}
                  </span>
                </p>
              </div>
            )}
            <p className="text-xs text-gray-500">
              You can join 5 minutes before the scheduled time
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Join screen
  if (step === 'join') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardHeader className="text-center">
            <CardTitle className="text-white">Join Meeting</CardTitle>
            <p className="text-gray-400 text-sm mt-1">Room: {roomId}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {meetingInfo && (
              <div className="bg-gray-700/50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-400">Meeting with</p>
                <p className="font-medium text-white">{meetingInfo.leadName}</p>
                <p className="text-sm text-gray-400 mt-2">Hosted by {meetingInfo.hostName}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-300">Your Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="mt-1 bg-gray-700 border-gray-600 text-white"
                disabled={role === 'host'}
                data-testid="name-input"
              />
            </div>
            
            <div className="text-center">
              <Badge className={role === 'host' ? 'bg-red-600' : 'bg-gray-600'}>
                Joining as {role}
              </Badge>
            </div>
            
            <Button
              onClick={joinMeeting}
              className="w-full bg-red-600 hover:bg-red-700"
              disabled={isConnecting || !name.trim()}
              data-testid="join-now-btn"
            >
              {isConnecting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Connecting...</>
              ) : (
                'Join Now'
              )}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              Camera and microphone permissions will be requested
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Waiting for host
  if (step === 'waiting') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 text-red-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-semibold mb-2 text-white">Waiting for Host</h2>
            <p className="text-gray-400 mb-4">The meeting will start when the host joins</p>
            
            <div className="flex justify-center mb-4">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="w-48 h-36 rounded-lg bg-gray-700 object-cover"
              />
            </div>
            
            <div className="flex justify-center gap-3 mb-4">
              <Button variant={isMuted ? 'destructive' : 'secondary'} size="sm" onClick={toggleMute}>
                {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              <Button variant={isVideoOff ? 'destructive' : 'secondary'} size="sm" onClick={toggleVideo}>
                {isVideoOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
              </Button>
            </div>
            
            <Button variant="outline" onClick={leaveMeeting} className="border-gray-600 text-gray-300">
              Leave
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Meeting ended
  if (step === 'ended') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <PhoneOff className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2 text-white">Meeting Ended</h2>
            <p className="text-gray-400 mb-4">Thank you for joining</p>
            {role === 'host' && (
              <p className="text-sm text-green-400 mb-4">✓ Summary has been saved</p>
            )}
            <Button onClick={() => navigate(role === 'host' ? '/leads-board' : '/')} className="bg-red-600 hover:bg-red-700">
              {role === 'host' ? 'Back to CRM' : 'Close'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active meeting
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Toaster position="top-center" />
      {/* Guest Knock Admit Popup */}
      {role === 'host' && knockRequests.map((knock, idx) => (
        <div key={knock.guestIdentity} style={{
          position: 'fixed', bottom: `${20 + idx * 120}px`, right: '20px',
          background: '#1e1e2e', border: '1px solid #7c3aed', borderRadius: '12px',
          padding: '16px 20px', zIndex: 9999, minWidth: '280px',
          boxShadow: '0 4px 20px rgba(124,58,237,0.3)'
        }}>
          <p style={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}>
            🔔 {knock.guestName} wants to join
          </p>
          <p style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>Waiting to be admitted</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleAdmit(knock, true)} style={{
              background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8,
              padding: '6px 16px', cursor: 'pointer', fontWeight: 600, flex: 1
            }}>Admit</button>
            <button onClick={() => handleAdmit(knock, false)} style={{
              background: '#333', color: '#aaa', border: 'none', borderRadius: 8,
              padding: '6px 16px', cursor: 'pointer', flex: 1
            }}>Deny</button>
          </div>
        </div>
      ))}
      
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="destructive" className="animate-pulse bg-red-600">
            <span className="w-2 h-2 bg-white rounded-full mr-2 inline-block" />LIVE
          </Badge>
          <span className="text-white text-sm truncate max-w-[200px]">
            {meetingInfo?.leadName || 'Meeting'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className={`w-2 h-2 rounded-full ${
            connectionStatus === 'connected' || connectionStatus === 'completed' ? 'bg-green-500' :
            connectionStatus === 'checking' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'
          }`} title={`Connection: ${connectionStatus}`} />
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400" />
            <span className="text-gray-400 text-sm">{participants.length}</span>
          </div>
        </div>
      </div>

      {/* Videos */}
      <div className="flex-1 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="relative bg-gray-800 rounded-xl overflow-hidden aspect-video">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          {!remoteParticipant && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Users className="h-16 w-16 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500">Waiting for participant...</p>
              </div>
            </div>
          )}
          <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/60 rounded text-white text-sm">
            {remoteParticipant?.name || 'Waiting...'}
          </div>
        </div>

        <div className="relative bg-gray-800 rounded-xl overflow-hidden aspect-video">
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
          />
          {isVideoOff && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-700">
              <div className="w-20 h-20 bg-gray-600 rounded-full flex items-center justify-center">
                <span className="text-2xl text-white">{name[0]?.toUpperCase()}</span>
              </div>
            </div>
          )}
          <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/60 rounded text-white text-sm flex items-center gap-2">
            {name} (You)
            {isMuted && <MicOff className="h-3 w-3 text-red-400" />}
          </div>
          {role === 'host' && <Badge className="absolute top-2 right-2 bg-red-600">Host</Badge>}
        </div>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 px-4 py-4 flex items-center justify-center gap-4">
        <Button
          variant={isMuted ? 'destructive' : 'secondary'}
          size="icon"
          className="h-12 w-12 rounded-full"
          onClick={toggleMute}
          data-testid="mute-btn"
        >
          {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </Button>
        
        <Button
          variant={isVideoOff ? 'destructive' : 'secondary'}
          size="icon"
          className="h-12 w-12 rounded-full"
          onClick={toggleVideo}
          data-testid="video-btn"
        >
          {isVideoOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
        </Button>
        
        <Button
          variant={isScreenSharing ? 'default' : 'secondary'}
          size="icon"
          className={`h-12 w-12 rounded-full hidden sm:flex ${isScreenSharing ? 'bg-blue-600' : ''}`}
          onClick={toggleScreenShare}
          data-testid="screen-share-btn"
        >
          {isScreenSharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
        </Button>
        
        <Button
          variant="destructive"
          size="icon"
          className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700"
          onClick={role === 'host' ? endMeeting : leaveMeeting}
          data-testid="end-call-btn"
        >
          <PhoneOff className="h-6 w-6" />
        </Button>
        
        <Button
          variant="secondary"
          size="icon"
          className="h-12 w-12 rounded-full"
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Link copied!');
          }}
          data-testid="copy-link-btn"
        >
          <Copy className="h-5 w-5" />
        </Button>
      </div>
      
      {role === 'host' && (
        <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 text-center">
          <p className="text-xs text-gray-400">As host, clicking end will end the meeting for everyone</p>
        </div>
      )}
    </div>
  );
};

export default MeetingRoomPage;
